import React, { useState } from 'react';
import { RutField, fillEmpty } from '../RutField';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Sparkles } from 'lucide-react';
import { JudgePicker } from './JudgePicker';
import { setActiveMatter } from '../../lib/activeMatter';
import { IntakeUpload } from './IntakeUpload';
import { CourtSelect } from './CourtSelect';
import { IntakeStrategyEditor, cleanStrategy } from './IntakeStrategyEditor';
import { CourtConfirmation } from './CourtConfirmation';

const COUNTRIES = [['chile', 'Chile'], ['ecuador', 'Ecuador'], ['argentina', 'Argentina'], ['mexico', 'México'], ['colombia', 'Colombia'], ['peru', 'Perú'], ['brasil', 'Brasil'], ['espana', 'España'], ['eeuu', 'EE.UU.']];
const TYPES = ['laboral', 'civil', 'penal', 'constitucional', 'tributario', 'administrativo', 'familia', 'comercial', 'disciplinario'];
const LEVELS = [['', 'Cualquier corte'], ['corte_suprema', 'Corte Suprema'], ['tribunal_constitucional', 'Tribunal Constitucional'], ['corte_apelaciones', 'Corte de Apelaciones'], ['corte_nacional', 'Corte Nacional'], ['corte_constitucional', 'Corte Constitucional']];
const INPUT = 'w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm';
const EMPTY = { client_id: '', new_client: '', rut: '', activity: '', title: '', description: '', case_number: '', matter_type: 'laboral', country: 'chile', court_level: '', assigned_judge_id: null, region: '', court_slug: '', court_name: '' };

const AiTag = ({ on, level, testid }) => (on ? (
  <span data-testid={testid} className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-amber-300"><Sparkles className="h-2.5 w-2.5" />propuesto por IA{level ? ` · confianza ${level}` : ''}</span>
) : null);

export const NewMatterDialog = ({ open, onOpenChange, clients, onCreated }) => {
  const { api } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [intake, setIntake] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [approved, setApproved] = useState(false);
  const [courtConfirmed, setCourtConfirmed] = useState(false);
  const set = (k) => (e) => { if (k === 'country') setCourtConfirmed(false); setF({ ...f, [k]: e.target.value, ...(k === 'country' ? { assigned_judge_id: null } : {}) }); };
  const changeCourt = (v) => { setCourtConfirmed(false); setF((x) => ({ ...x, ...v, assigned_judge_id: null, court_level: v.level || '' })); };
  const ai = (k) => intake?.proposal?.[k] != null && intake.proposal[k] !== '';
  const conf = (k) => intake?.proposal?.confidence?.[k];

  const onRut = (d) => setF((x) => fillEmpty(x, { new_client: d.name, activity: (d.activities || []).join(' · ') }));

  const applyIntake = (r) => {
    const p = r.proposal;
    setIntake(r);
    setStrategy(r.strategy || null);
    setApproved(false);
    setCourtConfirmed(false);
    setF((x) => ({
      ...x, country: 'chile', title: p.title || x.title, description: p.description || x.description, case_number: p.case_number || x.case_number,
      matter_type: p.matter_type || x.matter_type, client_id: p.client_id || x.client_id,
      new_client: p.client_id ? x.new_client : p.client_name || x.new_client, rut: p.client_id ? x.rut : p.client_rut || x.rut,
      region: p.region || x.region, court_slug: p.court_slug || '', court_name: p.court_name || p.court_text || '', assigned_judge_id: null,
    }));
    if (p.court_text && !p.court_slug) toast.message(`Tribunal mencionado: «${p.court_text}». Elígelo manualmente en el selector.`);
  };

  const reset = () => { setF(EMPTY); setIntake(null); setStrategy(null); setApproved(false); setCourtConfirmed(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.client_id && f.new_client.trim().length < 2) return toast.error('Elige o escribe un cliente');
    if (f.title.trim().length < 3) return toast.error('El título debe tener al menos 3 caracteres');
    if (strategy && !approved && !window.confirm('No aprobaste la estrategia propuesta: el caso se guardará con los documentos pero sin esa estrategia. ¿Continuar?')) return;
    setSaving(true);
    try {
      let clientId = f.client_id;
      if (!clientId) {
        const company = parseInt(f.rut.replace(/[^0-9kK]/g, '').slice(0, -1) || '0', 10) >= 50000000;
        const c = await api.post('/crm/clients', {
          type: company ? 'persona_juridica' : 'persona_natural', name: f.new_client.trim(), country: f.country,
          rut: f.rut || null, notes: f.activity ? `Actividad SII: ${f.activity}` : null
        });
        clientId = c.data.client.id;
      }
      const chile = f.country === 'chile';
      const created = await api.post('/crm/matters', {
        client_id: clientId, title: f.title.trim(), matter_type: f.matter_type, country: f.country,
        description: f.description.trim() || null, case_number: f.case_number.trim() || null,
        court_level: f.court_level || null, assigned_judge_id: f.assigned_judge_id,
        region: chile ? f.region || null : null, court_slug: chile ? f.court_slug || null : null,
        court: chile ? f.court_name || null : null, court_confirmed: chile && courtConfirmed,
        next_hearing: intake?.proposal?.next_hearing || null, estimated_value: intake?.proposal?.estimated_value || null,
        sala_numero: chile && f.court_slug && f.court_slug === intake?.proposal?.court_slug ? intake?.proposal?.sala_numero || null : null,
        intake_id: intake?.intake_id || null, intake_strategy: strategy && approved ? cleanStrategy(strategy) : null,
      });
      if (created.data?.matter) setActiveMatter(created.data.matter);
      const n = created.data?.attached_documents;
      toast.success(n ? `Caso creado con ${n} documento(s) en el expediente` : 'Caso creado, enviado a Cerebro y fijado como causa activa');
      onCreated();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear el caso');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-matter-dialog" className="grid-cols-[minmax(0,1fr)] w-[calc(100vw-1rem)] bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader><DialogTitle className="text-white">Nuevo caso</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="min-w-0 w-full space-y-3 [&>*]:min-w-0">
          <IntakeUpload onResult={applyIntake} disabled={saving} />
          <select data-testid="matter-client-select" value={f.client_id} onChange={set('client_id')} className={INPUT}>
            <option value="">+ Nuevo cliente</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {f.client_id && <AiTag on={f.client_id === intake?.proposal?.client_id} level={conf('cliente')} testid="ai-tag-existing-client" />}
          {!f.client_id && (
            <div className="space-y-2">
              <RutField testid="matter-client-rut-input" value={f.rut} onChange={(v) => setF((x) => ({ ...x, rut: v }))} onLookup={onRut} placeholder="RUT del cliente (autocompleta)" className={INPUT} />
              <input data-testid="matter-new-client-input" value={f.new_client} onChange={set('new_client')} placeholder="Nombre o razón social" className={INPUT} />
              <AiTag on={ai('client_name')} level={conf('cliente')} testid="ai-tag-client" />
              {f.activity && <p data-testid="matter-client-activity" className="text-xs text-slate-400">Actividad: {f.activity}</p>}
            </div>
          )}
          <div>
            <input data-testid="matter-title-input" value={f.title} onChange={set('title')} placeholder="Título del caso (ej. Despido injustificado Pérez)" className={INPUT} />
            <AiTag on={ai('title')} level={conf('titulo')} testid="ai-tag-title" />
          </div>
          {(intake || f.description) && (
            <textarea data-testid="matter-description-input" rows={3} value={f.description} onChange={set('description')} placeholder="Hechos del caso" className={INPUT} />
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <select data-testid="matter-type-select" value={f.matter_type} onChange={set('matter_type')} className={`${INPUT} capitalize`}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <AiTag on={ai('matter_type')} level={conf('materia')} testid="ai-tag-type" />
            </div>
            <select data-testid="matter-country-select" value={f.country} onChange={set('country')} className={INPUT}>
              {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <input data-testid="matter-case-number-input" value={f.case_number} onChange={set('case_number')} placeholder="ROL / RIT / RUC (opcional)" className={INPUT} />
          {f.country === 'chile' ? (
            <div>
              {intake ? <CourtConfirmation value={f} review={intake.proposal?.court_review} confirmed={courtConfirmed} onChange={changeCourt} onConfirm={() => setCourtConfirmed(true)} /> : <CourtSelect region={f.region} courtSlug={f.court_slug} onChange={changeCourt} />}
              {!courtConfirmed && <AiTag on={ai('court_slug')} level={conf('tribunal')} testid="ai-tag-court" />}
            </div>
          ) : (
            <select data-testid="matter-court-level-select" value={f.court_level} onChange={set('court_level')} className={INPUT}>
              {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
          <div>
            <p className="text-xs text-slate-400 mb-1">Juez asignado</p>
            <JudgePicker testid="new-matter-judge-picker" country={f.country} caseType={f.matter_type} courtLevel={f.court_level} courtSlug={f.country === 'chile' ? f.court_slug : null} value={f.assigned_judge_id} onChange={(v) => setF({ ...f, assigned_judge_id: v })} />
          </div>
          {strategy && <IntakeStrategyEditor value={strategy} onChange={setStrategy} approved={approved} onApprove={setApproved} />}
          <button data-testid="matter-submit-btn" type="submit" disabled={saving} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}Crear caso
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
